import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

import { WorkflowApiService } from '../services/workflow-api.service';
import { WorkflowFolderApiService } from '../services/workflow-folder-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import {
  WorkflowResponse,
  WorkflowStatus,
  WorkflowObjectType,
  WorkflowFolderResponse,
  WorkflowDuplicateRequest,
  WorkflowTemplateCreateRequest,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_OBJECT_TYPE_LABELS,
  WORKFLOW_OBJECT_TYPES_ENABLED
} from '../models/workflow.model';
import { PageResponse } from '../../../auth-pages/models/user.model';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../../shared/directives/disable-without-permission.directive';
import { WorkflowDetailModalComponent } from '../workflow-detail-modal/workflow-detail-modal.component';
import {
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../shared/services/user-directory-api.service';

@Component({
  selector: 'app-workflow-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, HasPermissionDirective, DisableWithoutPermissionDirective, WorkflowDetailModalComponent],
  templateUrl: './workflow-list.component.html'
})
export class WorkflowListComponent implements OnInit, OnDestroy {
  workflows: WorkflowResponse[] = [];
  folders: WorkflowFolderResponse[] = [];

  loading = false;
  error = '';

  // Paginación
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // Filtros
  searchTerm = '';
  selectedStatus: WorkflowStatus | '' = '';
  selectedObjectType: WorkflowObjectType | '' = '';
  selectedFolderId: number | '' = '';

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Modales
  showDuplicateModal = false;
  duplicatingWorkflow: WorkflowResponse | null = null;
  duplicateName = '';
  duplicateLoading = false;
  duplicateError = '';

  showTemplateModal = false;
  templateWorkflow: WorkflowResponse | null = null;
  templateName = '';
  templateDescription = '';
  templateCategory = '';
  templateLoading = false;
  templateError = '';

  /**
   * INC_CRM_020 (4) inc8 — diálogo de eliminación con contraseña. Mismo patrón que el borrado
   * de negocios (INC_CRM_023 inc16): el backend re-autentica y solo acepta workflows inactivos.
   */
  showDeleteModal = false;
  deleteTarget: WorkflowResponse | null = null;
  deletePassword = '';
  deleteError = '';
  deleting = false;
  showDeletePassword = false;

  readonly statusOptions: { value: WorkflowStatus | ''; label: string }[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'PAUSED', label: 'Pausado' },
    { value: 'ARCHIVED', label: 'Archivado' }
  ];

  /**
   * El filtro mostraba «Ticket» DOS VECES: se añadía a mano cuando TICKET todavía no estaba
   * habilitado y, al habilitarlo, entró también en `WORKFLOW_OBJECT_TYPES_ENABLED`. Mismo defecto
   * que ya se corrigió en `workflow-form` (INC_CRM_020 (9) inc47): la lista habilitada es la
   * única fuente.
   */
  readonly objectTypeOptions: { value: WorkflowObjectType | ''; label: string; disabled: boolean }[] = [
    { value: '', label: 'Todos los tipos', disabled: false },
    ...WORKFLOW_OBJECT_TYPES_ENABLED.map(t => ({ value: t, label: WORKFLOW_OBJECT_TYPE_LABELS[t], disabled: false }))
  ];

  constructor(
    private workflowApi: WorkflowApiService,
    private folderApi: WorkflowFolderApiService,
    private authService: AuthService,
    private toast: ToastService,
    private userDirectory: UserDirectoryApiService,
    private confirmDialog: ConfirmDialogService,
    private router: Router
  ) {}

  // ── Detalle (INC_CRM_020 (2) inc1 + inc4) ─────────────────────────────────
  //
  // El detalle es un MODAL con pestañas Información + Línea de tiempo, igual que en
  // contactos, leads, segmentos, formularios y business. Antes era una página propia con
  // 13 botones repetidos de esta misma lista.

  showDetailModal = false;
  selectedWorkflow: WorkflowResponse | null = null;
  selectedOwnerName = '';
  /** Fila cuyo menú "⋯" está abierto (Constructor / Inscripciones / Pruebas). */
  openRowMenuId: number | null = null;

  verDetalle(workflow: WorkflowResponse): void {
    this.openRowMenuId = null;
    this.selectedWorkflow = workflow;
    this.selectedOwnerName = '';
    this.showDetailModal = true;
    if (workflow.idOwnerUser) this.resolveOwnerName(workflow.idOwnerUser);
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.selectedWorkflow = null;
    this.selectedOwnerName = '';
  }

  /** "Editar" del modal: cierra y navega al formulario, que sí es página completa. */
  editarDesdeDetalle(workflow: WorkflowResponse): void {
    this.cerrarDetalle();
    this.router.navigate(['/automation/workflows', workflow.id, 'editar']);
  }

  /**
   * Resuelve el nombre del responsable por id contra el directorio de la empresa. Antes traía
   * una página de /api/auth/users/search (gateado con `users:VIEW`) y buscaba el id dentro: con
   * un rol sin ese permiso la respuesta era 403 y el nombre se quedaba vacío.
   */
  private resolveOwnerName(id: number): void {
    this.userDirectory.fetchUsersByIds([id])
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (users) => {
          const u = users.find((x) => x.id === id);
          if (u) this.selectedOwnerName = directoryUserLabel(u);
        },
        error: () => {}
      });
  }

  /**
   * INC_CRM_020 (7) inc22 — el menú "⋯" de las últimas filas mostraba 2 de las 4 acciones.
   *
   * <p>Era un dropdown `absolute` dentro de una tarjeta con `overflow-hidden` (necesario para
   * que las esquinas redondeadas recorten la cabecera de la tabla) y, por encima, del contenedor
   * con scroll del layout. Alternar las clases de overflow no basta: hay más de un ancestro que
   * recorta y, además, quitar el `overflow-x-auto` de la tabla resetea su scroll horizontal
   * justo cuando el usuario acaba de desplazarse hasta la columna de acciones.</p>
   *
   * <p>Se pasa a `position: fixed`, que escapa de CUALQUIER ancestro con overflow, con la
   * posición calculada desde el rectángulo del botón: alineado a su borde derecho y desplegado
   * hacia arriba cuando abajo no cabe entero. Al ir anclado a coordenadas de viewport, el menú
   * se cierra si la página cambia de tamaño o se desplaza.</p>
   */
  private static readonly ROW_MENU_WIDTH_PX = 224;  // w-56
  private static readonly ROW_MENU_HEIGHT_PX = 200;
  private static readonly ROW_MENU_GAP_PX = 4;

  rowMenuTop = 0;
  rowMenuLeft = 0;

  toggleRowMenu(id: number, event: Event): void {
    event.stopPropagation();
    if (this.openRowMenuId === id) {
      this.openRowMenuId = null;
      return;
    }
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      const rect = target.getBoundingClientRect();
      const { ROW_MENU_WIDTH_PX: w, ROW_MENU_HEIGHT_PX: h, ROW_MENU_GAP_PX: gap } = WorkflowListComponent;
      const cabeAbajo = window.innerHeight - rect.bottom >= h + gap;
      const top = cabeAbajo ? rect.bottom + gap : rect.top - h - gap;
      this.rowMenuTop = Math.max(gap, Math.min(top, window.innerHeight - h - gap));
      this.rowMenuLeft = Math.max(gap, Math.min(rect.right - w, window.innerWidth - w - gap));
    }
    this.openRowMenuId = id;
  }

  @HostListener('document:click')
  onDocumentClick(): void { this.openRowMenuId = null; }

  // Anclado a coordenadas de viewport: si la página se mueve, el menú dejaría de apuntar a su fila.
  @HostListener('window:resize')
  onViewportChange(): void { this.openRowMenuId = null; }

  /**
   * `scroll` no burbujea, así que un @HostListener no ve el del contenedor del layout ni el
   * horizontal de la tabla — justo los dos que mueven la fila bajo un menú posicionado con
   * `fixed`. En fase de CAPTURA sí llegan todos.
   */
  private readonly cerrarMenuAlScroll = (): void => {
    if (this.openRowMenuId !== null) {
      this.openRowMenuId = null;
    }
  };

  ngOnInit(): void {
    document.addEventListener('scroll', this.cerrarMenuAlScroll, true);

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadWorkflows();
    });

    this.loadFolders();
    this.loadWorkflows();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.cerrarMenuAlScroll, true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  loadWorkflows(): void {
    this.loading = true;
    this.error = '';

    const filters = {
      businessId: this.businessId,
      page: this.currentPage,
      size: this.pageSize,
      ...(this.selectedStatus && { status: this.selectedStatus }),
      ...(this.selectedObjectType && { objectType: this.selectedObjectType }),
      ...(this.selectedFolderId !== '' && { folderId: Number(this.selectedFolderId) }),
      ...(this.searchTerm && { searchTerm: this.searchTerm })
    };

    this.workflowApi.getWorkflows(filters).subscribe({
      next: (response) => {
        if (Array.isArray(response)) {
          this.workflows = response;
          this.totalElements = response.length;
          this.totalPages = 1;
        } else {
          const paged = response as PageResponse<WorkflowResponse>;
          this.workflows = paged.content;
          this.totalElements = paged.totalElements;
          this.totalPages = paged.totalPages;
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los workflows.';
        this.loading = false;
      }
    });
  }

  /** Solo carpetas activas para el dropdown de filtros (inc 24). */
  get activeFolders(): WorkflowFolderResponse[] {
    return this.folders.filter(f => f.isActive);
  }

  private loadFolders(): void {
    this.folderApi.getFolders(this.businessId).subscribe({
      next: (folders) => { this.folders = folders; },
      error: () => {}
    });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadWorkflows();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.selectedObjectType = '';
    this.selectedFolderId = '';
    this.currentPage = 0;
    this.loadWorkflows();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.loadWorkflows();
  }

  // ── Acciones de estado ────────────────────────────────────────────────────

  activate(workflow: WorkflowResponse): void {
    this.clearMessages();
    this.workflowApi.activate(workflow.id).subscribe({
      next: (updated) => {
        this.updateInList(updated);
        this.toast.success({ title: 'Workflow activado', message: `"${updated.name}" ya está inscribiendo y procesando registros.` });
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudo activar el workflow.';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  pause(workflow: WorkflowResponse): void {
    this.clearMessages();
    this.workflowApi.pause(workflow.id).subscribe({
      next: (updated) => {
        this.updateInList(updated);
        this.toast.success({ title: 'Workflow pausado', message: `"${updated.name}" dejó de inscribir y ejecutar registros.` });
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudo pausar el workflow.';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  /**
   * INC_CRM_020 (4) inc11 — archivar y restaurar usan AHORA el mismo diálogo del sistema.
   *
   * <p>QA reportó que las dos acciones se confirmaban de forma distinta. Y era así: archivar
   * abría un modal propio de este módulo (`fixed inset-0` escrito a mano en la plantilla) y
   * restaurar no pedía confirmación ninguna, pese a que ambas cambian el estado del workflow.
   * Las dos pasan por `ConfirmDialogService`, que es el diálogo único del sistema, y el éxito
   * va siempre por `ToastService`.</p>
   */
  async restore(workflow: WorkflowResponse): Promise<void> {
    this.clearMessages();
    const ok = await this.confirmDialog.confirm({
      title: 'Restaurar workflow',
      message: `"${workflow.name}" volverá a estar disponible como borrador, con toda su configuración. `
             + 'No se reanudan las inscripciones que se hubieran cancelado al archivarlo.',
      confirmText: 'Restaurar',
      variant: 'info'
    });
    if (!ok) return;

    this.workflowApi.restore(workflow.id).subscribe({
      next: (updated) => {
        this.updateInList(updated);
        this.toast.success({ title: 'Workflow restaurado', message: `"${updated.name}" vuelve a estar disponible como borrador.` });
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudo restaurar el workflow.';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  async archive(workflow: WorkflowResponse): Promise<void> {
    this.clearMessages();
    const ok = await this.confirmDialog.confirm({
      title: 'Archivar workflow',
      message: `¿Archivar "${workflow.name}"? Quedará inactivo: podrás verlo y restaurarlo, pero no ejecutarse.`,
      confirmText: 'Archivar',
      variant: 'warning'
    });
    if (!ok) return;
    this.ejecutarArchivado(workflow, false);
  }

  /**
   * El backend rechaza el archivado si quedan inscripciones en curso
   * (`WORKFLOW_HAS_ACTIVE_ENROLLMENTS`). Ese rechazo no es un error que mostrar y ya: es la
   * segunda pregunta del flujo, así que se vuelve a pedir confirmación —con el número de
   * inscripciones afectadas— y se reintenta con `force`.
   */
  private ejecutarArchivado(workflow: WorkflowResponse, force: boolean): void {
    this.workflowApi.archive(workflow.id, force).subscribe({
      next: (updated) => {
        this.updateInList(updated);
        this.toast.success({
          title: 'Workflow archivado',
          message: force
            ? `"${updated.name}": las inscripciones en curso se cancelaron.`
            : `"${updated.name}" ya no aparece en el listado activo; puedes restaurarlo cuando quieras.`
        });
      },
      error: async (err) => {
        const body = err?.error ?? {};
        const code = body?.code ?? body?.errorCode ?? body?.details?.code ?? '';
        if (!force && code === 'WORKFLOW_HAS_ACTIVE_ENROLLMENTS') {
          const enCurso = Number(body?.details?.activeEnrollments ?? 0);
          const cuantas = enCurso > 0
            ? `${enCurso} inscripción(es) en curso`
            : 'inscripciones en curso';
          const forzar = await this.confirmDialog.confirm({
            title: 'Hay inscripciones en curso',
            message: `"${workflow.name}" tiene ${cuantas}. Si continúas se cancelarán y quedará `
                   + 'registrado en el historial.',
            confirmText: 'Cancelar inscripciones y archivar',
            variant: 'danger'
          });
          if (forzar) this.ejecutarArchivado(workflow, true);
          return;
        }
        this.error = body?.message || 'No se pudo archivar el workflow.';
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  // ── Eliminar (INC_CRM_020 (4) inc8) ───────────────────────────────────────
  //
  // El listado solo permitía archivar, así que los workflows de prueba se acumulaban sin
  // forma de retirarlos. La acción existe ahora para todas las filas, pero solo se habilita
  // sobre workflows YA inactivados y exige la contraseña de la cuenta: el borrado es físico
  // (arrastra versiones, nodos, inscripciones y ejecuciones por CASCADE) y no se deshace.

  /** Un workflow ACTIVE está inscribiendo y ejecutando: hay que pausarlo o archivarlo antes. */
  canDelete(w: WorkflowResponse): boolean {
    return w.status !== 'ACTIVE';
  }

  abrirEliminar(workflow: WorkflowResponse): void {
    this.openRowMenuId = null;
    if (!this.canDelete(workflow)) {
      this.toast.warning({
        title: 'El workflow está activo',
        message: `Pausa o archiva "${workflow.name}" antes de eliminarlo.`
      });
      return;
    }
    this.deleteTarget = workflow;
    this.deletePassword = '';
    this.deleteError = '';
    this.showDeletePassword = false;
    this.showDeleteModal = true;
  }

  toggleDeletePassword(): void {
    this.showDeletePassword = !this.showDeletePassword;
  }

  cancelarEliminar(): void {
    if (this.deleting) return;
    this.showDeleteModal = false;
    this.deleteTarget = null;
    this.deletePassword = '';
    this.deleteError = '';
  }

  confirmarEliminar(): void {
    const w = this.deleteTarget;
    if (!w) return;

    const pwd = this.deletePassword.trim();
    if (!pwd) {
      this.deleteError = 'Ingresa tu contraseña para confirmar.';
      return;
    }

    this.deleting = true;
    this.deleteError = '';

    this.workflowApi.deleteWithPassword(w.id, pwd).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.deletePassword = '';
        if (this.selectedWorkflow?.id === w.id) this.cerrarDetalle();
        // Si la página se queda sin filas al borrar la última, se retrocede una.
        if (this.currentPage > 0 && this.workflows.length === 1) this.currentPage--;
        this.loadWorkflows();
        this.toast.deleted('Workflow', `"${w.name}" se eliminó junto con sus versiones e inscripciones.`);
      },
      error: (err) => {
        this.deleting = false;
        // 401 (contraseña incorrecta): el diálogo sigue abierto con el error dentro, para
        // que el usuario reintente sin perder el contexto.
        if (err?.status === 401) {
          this.deleteError = err?.error?.message?.trim() || 'Contraseña incorrecta.';
          return;
        }
        this.deleteError = err?.error?.message?.trim() || 'No se pudo eliminar el workflow.';
      }
    });
  }

  // ── Duplicar ──────────────────────────────────────────────────────────────

  openDuplicateModal(workflow: WorkflowResponse): void {
    this.duplicatingWorkflow = workflow;
    this.duplicateName = `${workflow.name} (copia)`;
    this.duplicateError = '';
    this.showDuplicateModal = true;
  }

  confirmDuplicate(): void {
    if (!this.duplicatingWorkflow || !this.duplicateName.trim()) return;
    this.duplicateLoading = true;
    this.duplicateError = '';

    const request: WorkflowDuplicateRequest = { name: this.duplicateName.trim() };
    this.workflowApi.duplicate(this.duplicatingWorkflow.id, request).subscribe({
      next: (created) => {
        this.workflows = [created, ...this.workflows];
        this.closeDuplicateModal();
        this.toast.created('Workflow', `Se creó "${created.name}" a partir del original.`);
      },
      error: (err) => {
        this.duplicateError = err.error?.message || 'No se pudo duplicar el workflow.';
        this.duplicateLoading = false;
      }
    });
  }

  closeDuplicateModal(): void {
    this.showDuplicateModal = false;
    this.duplicatingWorkflow = null;
    this.duplicateName = '';
    this.duplicateLoading = false;
    this.duplicateError = '';
  }

  // ── Guardar como plantilla ────────────────────────────────────────────────

  openTemplateModal(workflow: WorkflowResponse): void {
    this.templateWorkflow = workflow;
    this.templateName = workflow.name;
    this.templateDescription = workflow.description ?? '';
    this.templateCategory = workflow.templateCategory ?? '';
    this.templateError = '';
    this.showTemplateModal = true;
  }

  confirmSaveTemplate(): void {
    if (!this.templateWorkflow || !this.templateName.trim()) return;
    this.templateLoading = true;
    this.templateError = '';

    const request: WorkflowTemplateCreateRequest = {
      name: this.templateName.trim(),
      description: this.templateDescription || undefined,
      templateCategory: this.templateCategory || undefined
    };

    this.workflowApi.saveAsTemplate(this.templateWorkflow.id, request).subscribe({
      next: () => {
        this.closeTemplateModal();
        this.toast.created('Plantilla', 'El workflow quedó disponible como plantilla reutilizable.');
      },
      error: (err) => {
        this.templateError = err.error?.message || 'No se pudo guardar como plantilla.';
        this.templateLoading = false;
      }
    });
  }

  closeTemplateModal(): void {
    this.showTemplateModal = false;
    this.templateWorkflow = null;
    this.templateName = '';
    this.templateDescription = '';
    this.templateCategory = '';
    this.templateLoading = false;
    this.templateError = '';
  }

  // ── Utilidades ────────────────────────────────────────────────────────────

  getStatusLabel(status: WorkflowStatus): string {
    return WORKFLOW_STATUS_LABELS[status] ?? status;
  }

  getStatusClass(status: WorkflowStatus): string {
    switch (status) {
      case 'ACTIVE':   return 'bg-green-100 text-green-700';
      case 'PAUSED':   return 'bg-yellow-100 text-yellow-700';
      case 'ARCHIVED': return 'bg-red-100 text-red-700';
      default:         return 'bg-gray-100 text-gray-700';
    }
  }

  getObjectTypeLabel(type: WorkflowObjectType): string {
    return WORKFLOW_OBJECT_TYPE_LABELS[type] ?? type;
  }

  getObjectTypeClass(type: WorkflowObjectType): string {
    switch (type) {
      case 'CONTACT':      return 'bg-blue-100 text-blue-700';
      case 'COMPANY':      return 'bg-purple-100 text-purple-700';
      case 'DEAL':         return 'bg-orange-100 text-orange-700';
      case 'QUOTE':        return 'bg-teal-100 text-teal-700';
      case 'SUBSCRIPTION': return 'bg-indigo-100 text-indigo-700';
      default:             return 'bg-gray-100 text-gray-700';
    }
  }

  getFolderName(folderId: number | null | undefined): string {
    if (!folderId) return '—';
    const folder = this.folders.find(f => f.id === folderId);
    return folder?.name ?? '—';
  }

  canActivate(w: WorkflowResponse): boolean {
    return w.status === 'DRAFT' || w.status === 'PAUSED';
  }

  canPause(w: WorkflowResponse): boolean {
    return w.status === 'ACTIVE';
  }

  canEdit(w: WorkflowResponse): boolean {
    return w.status !== 'ARCHIVED';
  }

  canArchive(w: WorkflowResponse): boolean {
    return w.status !== 'ARCHIVED';
  }

  canRestore(w: WorkflowResponse): boolean {
    return w.status === 'ARCHIVED';
  }

  formatTags(tags: string[]): string[] {
    return tags?.slice(0, 3) ?? [];
  }

  hasMoreTags(tags: string[]): number {
    return (tags?.length ?? 0) > 3 ? tags.length - 3 : 0;
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  private updateInList(updated: WorkflowResponse): void {
    const idx = this.workflows.findIndex(w => w.id === updated.id);
    if (idx !== -1) this.workflows[idx] = updated;
  }

  private clearMessages(): void {
    this.error = '';
  }
}
